import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Pencil, Trash2, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  usePatientDiaries,
  useCreateDiary,
  useUpdateDiary,
  useDeleteDiary,
  type PatientDiaryDTO,
} from "@/hooks/api/useDiaries";

interface Props {
  patientId: string;
}

export function DiariesSection({ patientId }: Props) {
  const { t } = useTranslation();
  const { data: diaries = [], isLoading } = usePatientDiaries(patientId);
  const createMutation = useCreateDiary();
  const updateMutation = useUpdateDiary();
  const deleteMutation = useDeleteDiary();

  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");
  const [editTarget, setEditTarget] = useState<PatientDiaryDTO | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PatientDiaryDTO | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!newTitle.trim() || !newContent.trim()) return;
    try {
      await createMutation.mutateAsync({ patientId, title: newTitle.trim(), content: newContent.trim() });
      setNewTitle("");
      setNewContent("");
      setShowCreate(false);
    } catch {
      toast.error(t("diaries.createError"));
    }
  };

  const handleUpdate = async () => {
    if (!editTarget) return;
    try {
      await updateMutation.mutateAsync({ id: editTarget.id, patientId, title: editTarget.title, content: editTarget.content });
      setEditTarget(null);
    } catch {
      toast.error(t("diaries.updateError"));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync({ id: deleteTarget.id, patientId });
      setDeleteTarget(null);
    } catch {
      toast.error(t("diaries.deleteError"));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-lg font-semibold">{t("diaries.title")}</h2>
        </div>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setShowCreate(true)}>
          <Plus className="h-3.5 w-3.5" />
          {t("diaries.new")}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner className="h-5 w-5" />
        </div>
      ) : diaries.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground text-sm">
            {t("diaries.empty")}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {diaries.map((diary) => (
            <Card key={diary.id} className="cursor-pointer" onClick={() => setExpanded(expanded === diary.id ? null : diary.id)}>
              <CardContent className="py-3 px-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{diary.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {diary.doctorName && `${diary.doctorName} · `}
                      {new Date(diary.createdAt).toLocaleDateString()}
                    </p>
                    {expanded === diary.id && (
                      <p className="text-sm mt-2 whitespace-pre-wrap">{diary.content}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0"
                      onClick={() => setEditTarget(diary)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-red-400 hover:text-red-500"
                      onClick={() => setDeleteTarget(diary)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("diaries.newTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder={t("diaries.titlePlaceholder")}
            />
            <Textarea
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              placeholder={t("diaries.contentPlaceholder")}
              rows={5}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleCreate} disabled={!newTitle.trim() || !newContent.trim() || createMutation.isPending}>
              {createMutation.isPending && <Spinner className="h-3 w-3 mr-1" />}
              {t("common.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("diaries.editTitle")}</DialogTitle>
          </DialogHeader>
          {editTarget && (
            <div className="space-y-3">
              <Input
                value={editTarget.title}
                onChange={(e) => setEditTarget({ ...editTarget, title: e.target.value })}
                placeholder={t("diaries.titlePlaceholder")}
              />
              <Textarea
                value={editTarget.content}
                onChange={(e) => setEditTarget({ ...editTarget, content: e.target.value })}
                placeholder={t("diaries.contentPlaceholder")}
                rows={5}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>{t("common.cancel")}</Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
              {updateMutation.isPending && <Spinner className="h-3 w-3 mr-1" />}
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("common.confirmDelete")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t("diaries.deleteConfirm")}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending && <Spinner className="h-3 w-3 mr-1" />}
              {t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
